import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { usePermissionGuardRedirect } from "../../../hooks/usePermissionGuard";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { useSelector } from "react-redux";
import PermissionsGate from "../../../utils/permissionGate";
import { RenderProjectStatus } from "../../../utils/CommonStatusItems";
import useDebounce from "../../../hooks/useDebounce";
import {
    GetPCProjectList,
    GetPCProjectListFilters,
} from "../../../services/ProjectCenterServices";
import Tablefilter from "../../common/Tablefilter";
import { tokens } from "../../../public/tokens";
import { processMilestoneData, RenderDepartment } from "../utils/ProjectCenterUtils";

const statusBarColor = {
    "Active": tokens.colors.states.success[500],
    "On Hold": tokens.colors.brand["karoo-gold"][500],
    "Complete": tokens.colors.brand.info?.[500] || tokens.colors.states.success[500],
    "Cancelled": tokens.colors.neutral.grey[500],
};

const milestoneDotColor = {
    "Not Started": tokens.colors.states.alert[500],
    "In Progress": tokens.colors.brand["karoo-gold"][500],
    "Complete": tokens.colors.states.success[500],
};

const STORAGE_KEY = "PCProjectTimeline";
const DEFAULT_STATUS_FILTER = { arr: ["Active"], checkObj: { Active: true } };
const PER_PAGE_OPTIONS = [10, 25, 50];

const getStorageKey = (storageKey) => `filterState_${storageKey}`;

// ============================================
// Layout
// ============================================
const Legend = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 260px;
    margin-bottom: 10px;
    justify-content: center;
    margin-top: 10px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${tokens.colors.neutral.grey[600]};
`;

const LegendDot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    display: inline-block;
    flex-shrink: 0;
`;

const TimelineWrap = styled.div`
    position: relative;
    margin-top: 40px;
`;
const NavigationButton = styled.button`
    height: 24px;
    width: 24px;
    border: none !important;
    border-radius: 50% !important;
    display: flex;
    align-items: center;
    color: ${tokens.colors.neutral.grey[700]} !important;
`;
const NoWrapSpan = styled.span`
    white-space: nowrap;
    `;
const PerPageSelect = styled.select`
    border: 1px solid ${tokens.colors.neutral.grey[200]} !important;
    appearance: none !important;
    min-width: 60px !important;
    cursor: pointer !important;
    height: 14px !important;
    max-height: 14px !important;
    min-height: 14px !important;
    user-select: none !important;
    padding-left: 8px !important;
    color: ${tokens.colors.neutral.grey[900]} !important;
    padding-right: 12px !important;
    box-sizing: content-box !important;
    direction: ltr !important;
    background-image: url(/static/media/arrow-down.a1bedf29.svg) !important;
    font-size: 13px !important;
`;
const AxisRow = styled.div`
    display: flex;
    margin-left: 260px;
    border-bottom: 1px solid var(--surfaces-border-divider, ${tokens.colors.neutral.grey[100]});
    padding-bottom: 6px;
    margin-bottom: 8px;
    position: relative;
`;

const AxisLabel = styled.div`
    position: absolute;
    left: ${({ $left }) => $left}%;
    transform: translateX(-50%);
    font-size: 12px;
    color: ${tokens.colors.neutral.grey[500]};
    white-space: nowrap;
`;

const ProjectRow = styled.div`
    display: flex;
    align-items: center;
    min-height: 56px;
    border-bottom: 1px solid var(--surfaces-border-divider, ${tokens.colors.neutral.grey[100]});
`;

const ProjectMeta = styled.div`
    width: 260px;
    flex-shrink: 0;
    padding-right: 12px;
    overflow: hidden;
`;

const ProjectName = styled.span`
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const TrackContainer = styled.div`
    position: relative;
    flex-grow: 1;
    height: 28px;
`;

const TrackBar = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ $left }) => $left}%;
    width: ${({ $width }) => $width}%;
    min-width: 6px;
    height: 10px;
    border-radius: 5px;
    background-color: ${({ $color }) => $color};
    opacity: 0.35;
`;

const TrackFill = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ $left }) => $left}%;
    width: ${({ $width }) => $width}%;
    height: 10px;
    border-radius: 5px;
    background-color: ${({ $color }) => $color};
`;

const MilestoneDot = styled.div`
    position: absolute;
    top: 50%;
    left: ${({ $left }) => $left}%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    border: 2px solid ${tokens.colors.neutral.white};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    z-index: 2;
`;

const TodayLine = styled.div`
    position: absolute;
    top: 20px;
    bottom: 0;
    left: calc(260px + (100% - 260px) * ${({ $left }) => $left} / 100);
    width: 2px;
    background-color: ${tokens.colors.states.alert[500]};
    z-index: 3;
    pointer-events: none;
`;

const TodayLineLabel = styled.div`
    position: absolute;
    top: -20px;
    left: calc(260px + (100% - 260px) * ${({ $left }) => $left} / 100);
    transform: translateX(-50%);
    font-size: 11px;
    color: ${tokens.colors.states.alert[500]};
    white-space: nowrap;
    z-index: 3;
`;

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: ${tokens.globals["spacing-md"]};
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

// ============================================
// Date scaling helpers
// ============================================

const toUnix = (dateStr) => (dateStr ? moment(dateStr).startOf("day").unix() : null);

/**
 * Builds monthly axis labels for the timeline chart between two Unix timestamps.
 *
 * @param {number} minUnix - Minimum Unix timestamp for the chart range.
 * @param {number} maxUnix - Maximum Unix timestamp for the chart range.
 * @returns {Array<{label: string, left: number}>} Array of month labels with clamped horizontal positions.
 */
const buildAxisLabels = (minUnix, maxUnix) => {
    const start = moment.unix(minUnix).startOf("month");
    const end = moment.unix(maxUnix).endOf("month");
    const labels = [];
    let cursor = start.clone();

    while (cursor.isSameOrBefore(end)) {
        const left = ((cursor.unix() - minUnix) / (maxUnix - minUnix)) * 100;
        labels.push({ label: cursor.format("MMM YYYY"), left: Math.max(0, Math.min(100, left)) });
        cursor = cursor.add(1, "month");
    }

    return labels;
};

const PCProjectTimeline = () => {
    usePermissionGuardRedirect(["projectcenterprojectsview"]);

    const [loading, setLoading] = useState(true);
    const [filtersHydrated, setFiltersHydrated] = useState(false);
    const [projects, setProjects] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 1000);
    const [department, setDepartment] = useState({ arr: [], checkObj: {} });
    const [status, setStatus] = useState(DEFAULT_STATUS_FILTER);
    const [accountable, setAccountable] = useState({ arr: [], checkObj: {} });
    const [responsible, setResponsible] = useState({ arr: [], checkObj: {} });
    const [template, setTemplate] = useState({ arr: [], checkObj: {} });
    const [filterData, setFilterData] = useState({
        DepartmentFilter: [],
        StatusFilter: [],
        TemplateFilter: [],
        UserFilter: [],
    });
    const givenPermissions = useSelector((state) => state.givenPermission);

    const hasNonWatcherPermission = () => {
        const permissions = [];
        Object.keys(givenPermissions).forEach((key) => {
            permissions.push(givenPermissions[key]);
        });
        return permissions.includes("projectcenternowatchview");
    };

    const [watchingOnly, setWatchingOnly] = useState(hasNonWatcherPermission);

    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(getStorageKey(STORAGE_KEY)));
            if (stored) {
                if (stored.department) setDepartment(stored.department);
                if (stored.status) setStatus(stored.status);
                if (stored.template) setTemplate(stored.template);
                if (stored.accountable) setAccountable(stored.accountable);
                if (stored.responsible) setResponsible(stored.responsible);
            }
        } catch (e) {
            // malformed or missing storage entry — fall back to defaults silently
        }
        setFiltersHydrated(true);
    }, []);

    useEffect(() => {
        if (!filtersHydrated) return;
        localStorage.setItem(
            getStorageKey(STORAGE_KEY),
            JSON.stringify({ department, status, template, accountable, responsible })
        );
    }, [filtersHydrated, department, status, template, accountable, responsible]);

    useEffect(() => {
        const source = axios.CancelToken.source();

        const getFilters = async () => {
            try {
                const res = await GetPCProjectListFilters(source.token);
                if (res.status === 200) {
                    setFilterData(res.data?.data || {});
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(error);
                }
            }
        };

        getFilters();

        return () => {
            source.cancel("Component unmounted");
        };
    }, []);

    useEffect(() => {
        if (!filtersHydrated) return;

        const source = axios.CancelToken.source();

        const getProjects = async () => {
            setLoading(true);
            try {
                const res = await GetPCProjectList(
                    {
                        page,
                        limit: perPage,
                        key: "name",
                        sort: "asc",
                        exportStatus: "false",
                        name: debouncedSearch,
                        viaDepartment: department.arr,
                        viaStatus: status.arr,
                        viaTemplate: template.arr,
                        viaAccountable: accountable.arr,
                        viaResponsible: responsible.arr,
                        isWatcher: watchingOnly,
                        forcedWatcher: !hasNonWatcherPermission()
                    },
                    source.token
                );
                if (res.status === 200) {
                    setProjects(res.data?.data_list?.data || []);
                    setTotalRows(res.data?.data_list?.total || 0);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(error);
                }
            } finally {
                setLoading(false);
            }
        };

        getProjects();

        return () => {
            source.cancel("Component unmounted");
        };
    }, [filtersHydrated, page, perPage, debouncedSearch, department, status, template, accountable, responsible, watchingOnly, hasNonWatcherPermission()]);

    // Reset pagination back to page 1 whenever a filter or search changes,
    // so the user doesn't get stuck on a now out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, department, status, template, accountable, responsible, watchingOnly, hasNonWatcherPermission()]);

    const projectsWithMetrics = useMemo(() => {
        return projects.map((project) => ({
            ...project,
            _metrics: processMilestoneData(project.milestones, project.start_date || project.created_at),
        }));
    }, [projects]);

    // Determine the date range to scale the chart against, based on the
    // current page's projects: earliest start/milestone date to latest
    // estimated end date, padded by two weeks on either side.
    const { minUnix, maxUnix } = useMemo(() => {
        const allDates = [];

        projectsWithMetrics.forEach((project) => {
            if (project.start_date || project.created_at) allDates.push(toUnix(project.start_date || project.created_at));
            (project.milestones || []).forEach((milestone) => {
                if (milestone.expected_date) allDates.push(toUnix(milestone.expected_date));
                if (milestone.cardinal_date) allDates.push(toUnix(milestone.cardinal_date));
            });
        });

        const validDates = allDates.filter(Boolean);

        if (!validDates.length) {
            const now = moment();
            return {
                minUnix: now.clone().subtract(1, "month").unix(),
                maxUnix: now.clone().add(3, "months").unix(),
            };
        }

        const min = Math.min(...validDates);
        const max = Math.max(...validDates);
        const padding = 14 * 24 * 60 * 60; // 2 weeks in seconds

        return { minUnix: min - padding, maxUnix: max + padding };
    }, [projectsWithMetrics]);

    const percentForUnix = (unixTime) => {
        if (!unixTime) return null;
        const pct = ((unixTime - minUnix) / (maxUnix - minUnix)) * 100;
        return Math.max(0, Math.min(100, pct));
    };

    const axisLabels = useMemo(() => buildAxisLabels(minUnix, maxUnix), [minUnix, maxUnix]);
    const todayLeft = percentForUnix(moment().unix());

    const resetFilter = () => {
        setSearch("");
        setDepartment({ arr: [], checkObj: {} });
        setStatus(DEFAULT_STATUS_FILTER);
        setTemplate({ arr: [], checkObj: {} });
        setAccountable({ arr: [], checkObj: {} });
        setResponsible({ arr: [], checkObj: {} });
    };

    const lastPage = Math.max(1, Math.ceil(totalRows / perPage));

    if (!filtersHydrated || (loading)) {
        return <SkeletonTicketList />;
    }

    return (
        <div
            className="tab-pane fade active show"
            id="pills-tab11"
            role="tabpanel"
            aria-labelledby="pills-tab1"
        >
            <div className="my-tickets-info-list Tickets-main-wrap">
                <div className="custom-table-div filter-search-icon card card-table-custom course-table-filter-sec submisson-table-bg">
                    <div className="search-filter-div">
                        <div className="search-filter-div-left">
                            <div className="system-administration-table table-responsive">
                                <div className="table-responsive-div">
                                    <div
                                        id="assessment-table-main_wrapper"
                                        className="dataTables_wrapper no-footer"
                                    >
                                        <div className="dataTables_wrapper no-footer">
                                            <div className="dataTables_filter">
                                                <label>
                                                    <input
                                                        type="search"
                                                        placeholder="Search"
                                                        onChange={(e) => setSearch(e.target.value)}
                                                        aria-controls="assessment-table-main"
                                                        value={search}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Tablefilter
                                    filterName=" Status"
                                    optionArr={filterData.StatusFilter}
                                    state={status}
                                    setState={setStatus}
                                    uniqueId="timelineStatus"
                                    renderFunction={(value) => RenderProjectStatus(value)}
                                />
                                <Tablefilter
                                    filterName=" Department"
                                    optionArr={filterData.DepartmentFilter}
                                    state={department}
                                    setState={setDepartment}
                                    uniqueId="timelineDepartment"
                                    renderLabelFunction={(value) => RenderDepartment(value, filterData && filterData["DepartmentFilter"] || [])}
                                />
                                <Tablefilter
                                    filterName=" Template"
                                    optionArr={filterData.TemplateFilter}
                                    state={template}
                                    setState={setTemplate}
                                    uniqueId="timelineTemplate"
                                />
                                <Tablefilter
                                    filterName=" Accountable"
                                    optionArr={filterData.UserFilter}
                                    state={accountable}
                                    setState={setAccountable}
                                    uniqueId="timelineAccountable"
                                    isSearchFilter
                                />
                                <Tablefilter
                                    filterName=" Responsible"
                                    optionArr={filterData.UserFilter}
                                    state={responsible}
                                    setState={setResponsible}
                                    uniqueId="timelineResponsible"
                                    isSearchFilter
                                />
                                <div className="reset-btn-group">
                                    <div className="button-reset dropdown-comman">
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            title="Reset"
                                            onClick={resetFilter}
                                        >
                                            <i className="fal fa-redo"></i>Reset
                                        </button>
                                    </div>
                                </div>
                                <div className="search-filter-div-right">
                        <div className=" filter-search-bar-blk">
                                <PermissionsGate
                                scopes={["projectcenternowatchview"]}
                                errorProps={{ disabled: true }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-primary mr-2"
                                    onClick={() => setWatchingOnly((prev) => !prev)}
                                    title={!hasNonWatcherPermission()
                                        ? "You do not have permissions to view projects which you are not responsible, accountable, the creator of or an assigned watcher."
                                        : "Projects you are watching, responsible for or accountable for"}
                                >
                                    <i className="fal fa-eye"></i>{" "}
                                    {watchingOnly || !hasNonWatcherPermission() ? "Watching Only" : "All Projects"}
                                </button>
                            </PermissionsGate>
                            </div>
                            </div>
                            </div>                            
                        </div>
                    </div>                    
                    {!projectsWithMetrics.length ? (
                        <div className="d-flex justify-content-center text-muted">No projects match the current filters.</div>
                    ) : (
                        <>
                            <TimelineWrap>
                                <AxisRow>
                                    {axisLabels.map((axisLabel, index) => (
                                        <AxisLabel key={index} $left={axisLabel.left}>
                                            {axisLabel.label}
                                        </AxisLabel>
                                    ))}
                                </AxisRow>

                                {todayLeft !== null && (
                                    <>
                                        <TodayLineLabel $left={todayLeft}>Today</TodayLineLabel>
                                        <TodayLine $left={todayLeft} />
                                    </>
                                )}

                                {projectsWithMetrics.map((project) => {
                                    const startUnix = toUnix(project.start_date || project.created_at);
                                    const endUnix = toUnix(                                        
                                            project._metrics.EndDateCardinal !== "-"
                                            ? project._metrics.EndDateCardinal
                                            : project._metrics.EndDateExpected !== "-"
                                            ? project._metrics.EndDateExpected
                                            : null
                                    );

                                    const barLeft = startUnix ? percentForUnix(startUnix) : 0;
                                    const barRight = endUnix ? percentForUnix(endUnix) : barLeft;
                                    const barWidth = Math.max(1, barRight - barLeft);
                                    const completedMilestones = (project.milestones || []).filter(
                                        (milestone) => milestone.status === "Complete"
                                    );
                                    const lastCompletedUnix = completedMilestones.length
                                        ? Math.max(
                                            ...completedMilestones
                                                .map((milestone) => toUnix(milestone.expected_date || milestone.cardinal_date))
                                                .filter(Boolean)
                                        )
                                        : null;
                                    const fillRight = lastCompletedUnix ? percentForUnix(lastCompletedUnix) : barLeft;
                                    const fillWidth = Math.max(0, fillRight - barLeft);

                                    const barColor = statusBarColor[project.status] || tokens.colors.neutral.grey[500];

                                    return (
                                        <ProjectRow key={project.id}>
                                            <ProjectMeta>
                                                <Link
                                                    to={`/project-center/projects/open/${project.id}`}
                                                    className="as-text-blue curser feature-name"
                                                    title={project.name}
                                                >
                                                    <ProjectName>{project.name}</ProjectName>
                                                </Link>
                                                <small className="text-muted-assessment">
                                                    {RenderDepartment(project.department, filterData && filterData["DepartmentFilter"] || [])?.html || "-"}| {project._metrics.completion}
                                                </small>
                                            </ProjectMeta>

                                            <TrackContainer>
                                                <TrackBar $left={barLeft} $width={barWidth} $color={barColor} />
                                                <TrackFill $left={barLeft} $width={fillWidth} $color={barColor} />

                                                {(project.milestones || []).map((milestone) => {
                                                    const milestoneUnix = toUnix(
                                                        milestone.expected_date || milestone.cardinal_date
                                                    );
                                                    const left = percentForUnix(milestoneUnix);
                                                    if (left === null) return null;

                                                    return (
                                                        <MilestoneDot
                                                            key={milestone.id}
                                                            $left={left}
                                                            $color={milestoneDotColor[milestone.status] || tokens.colors.neutral.grey[500]}
                                                            title={`${milestone.name} — ${milestone.status} (${moment(
                                                                milestone.expected_date || milestone.cardinal_date
                                                            ).format("DD MMM YYYY")})`}
                                                        />
                                                    );
                                                })}
                                            </TrackContainer>
                                        </ProjectRow>
                                    );
                                })}
                            </TimelineWrap>
                                <Legend>
                                    {Object.entries(milestoneDotColor).map(([label, color]) => (
                                        <LegendItem key={label}>
                                            <LegendDot $color={color} />
                                            {label}
                                        </LegendItem>
                                    ))}
                                </Legend>
                            <PaginationBar>         
                                <div></div>                          
                                <PaginationControls>
                                    <NoWrapSpan>Projects per page: </NoWrapSpan>
                                    <PerPageSelect
                                        className="form-control"
                                        value={perPage}
                                        onChange={(e) => setPerPage(Number(e.target.value))}
                                    >
                                        {PER_PAGE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option} per page
                                            </option>
                                        ))}
                                    </PerPageSelect>                                    
                                    <NoWrapSpan>
                                        Page {page} of {lastPage} &middot; {totalRows} projects
                                    </NoWrapSpan>
                                    <NavigationButton
                                        type="button"
                                        className="btn btn-white-bordered"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    >
                                        <i className="fal fa-angle-left"></i>
                                    </NavigationButton>
                                    <NavigationButton
                                        type="button"
                                        className="btn btn-white-bordered"
                                        disabled={page >= lastPage}
                                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                                    >
                                        <i className="fal fa-angle-right"></i>
                                    </NavigationButton>
                                </PaginationControls>
                            </PaginationBar>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PCProjectTimeline;